/**
 * tsl-gerstner — the TSL (node) versions of the Gerstner evaluators, built
 * per wave set with the loop unrolled JS-side (count ≤ 24 constants). One
 * implementation feeds the water material AND the caustics projector; the
 * math mirrors GerstnerWaves.ts exactly (same clock, same constants).
 */

import { cos, float, normalize, sin, vec3 } from 'three/tsl';
import type { GerstnerWave } from './GerstnerWaves.js';

type FloatNode = ReturnType<typeof float>;
type UniformLike = { mul(v: unknown): FloatNode };

/**
 * Runtime sea-state modulation as NODES (uniforms): mirrors
 * GerstnerWaves.WaveModulation — amplitude scale + heading rotation
 * (cos/sin of the angle). Omitted = the authored constants, folded at build.
 */
export interface TslWaveModulation {
  ampScale: FloatNode;
  dirCos: FloatNode;
  dirSin: FloatNode;
}

export interface TslGerstner {
  displacement(px: FloatNode, pz: FloatNode): { dx: FloatNode; dy: FloatNode; dz: FloatNode };
  normal(px: FloatNode, pz: FloatNode): ReturnType<typeof normalize>;
  jacobian(px: FloatNode, pz: FloatNode): FloatNode;
}

/** Build the three evaluators over a wave set and a shared time uniform. */
export function makeTslGerstner(waves: GerstnerWave[], uTime: UniformLike, mod?: TslWaveModulation): TslGerstner {
  if (mod) return makeModulatedTslGerstner(waves, uTime, mod);
  const phaseOf = (wv: GerstnerWave, px: FloatNode, pz: FloatNode): FloatNode =>
    float(wv.w).mul(px.mul(wv.dirX).add(pz.mul(wv.dirZ))).add(uTime.mul(wv.phi) as FloatNode);

  return {
    displacement(px, pz) {
      let dx: FloatNode = float(0);
      let dy: FloatNode = float(0);
      let dz: FloatNode = float(0);
      for (const wv of waves) {
        const phase = phaseOf(wv, px, pz);
        const c = cos(phase);
        dy = dy.add(sin(phase).mul(wv.amplitude));
        dx = dx.add(c.mul(wv.q * wv.amplitude * wv.dirX));
        dz = dz.add(c.mul(wv.q * wv.amplitude * wv.dirZ));
      }
      return { dx, dy, dz };
    },

    normal(px, pz) {
      let nx: FloatNode = float(0);
      let ny: FloatNode = float(1);
      let nz: FloatNode = float(0);
      for (const wv of waves) {
        const phase = phaseOf(wv, px, pz);
        const wa = wv.w * wv.amplitude;
        nx = nx.sub(cos(phase).mul(wa * wv.dirX));
        nz = nz.sub(cos(phase).mul(wa * wv.dirZ));
        ny = ny.sub(sin(phase).mul(wv.q * wa));
      }
      return normalize(vec3(nx, ny, nz));
    },

    jacobian(px, pz) {
      let jxx: FloatNode = float(1);
      let jzz: FloatNode = float(1);
      let jxz: FloatNode = float(0);
      for (const wv of waves) {
        const phase = phaseOf(wv, px, pz);
        const qaw = sin(phase).mul(wv.q * wv.amplitude * wv.w);
        jxx = jxx.sub(qaw.mul(wv.dirX * wv.dirX));
        jzz = jzz.sub(qaw.mul(wv.dirZ * wv.dirZ));
        jxz = jxz.sub(qaw.mul(wv.dirX * wv.dirZ));
      }
      return jxx.mul(jzz).sub(jxz.mul(jxz));
    },
  };
}

/**
 * The modulated evaluators: every wave heading is rotated by (dirCos, dirSin)
 * and every amplitude scaled by ampScale — the same arithmetic as the CPU
 * samplers with a WaveModulation, so a wind-driven sea still answers the
 * height query the buoyancy and the rides ask.
 */
function makeModulatedTslGerstner(waves: GerstnerWave[], uTime: UniformLike, mod: TslWaveModulation): TslGerstner {
  const terms = waves.map((wv) => ({
    wv,
    dirX: mod.dirCos.mul(wv.dirX).sub(mod.dirSin.mul(wv.dirZ)) as FloatNode,
    dirZ: mod.dirSin.mul(wv.dirX).add(mod.dirCos.mul(wv.dirZ)) as FloatNode,
    amp: mod.ampScale.mul(wv.amplitude) as FloatNode,
  }));
  const phaseOf = (t: (typeof terms)[number], px: FloatNode, pz: FloatNode): FloatNode =>
    float(t.wv.w).mul(px.mul(t.dirX).add(pz.mul(t.dirZ))).add(uTime.mul(t.wv.phi) as FloatNode);

  return {
    displacement(px, pz) {
      let dx: FloatNode = float(0);
      let dy: FloatNode = float(0);
      let dz: FloatNode = float(0);
      for (const t of terms) {
        const phase = phaseOf(t, px, pz);
        const c = cos(phase);
        dy = dy.add(sin(phase).mul(t.amp));
        const qa = c.mul(t.amp).mul(t.wv.q);
        dx = dx.add(qa.mul(t.dirX));
        dz = dz.add(qa.mul(t.dirZ));
      }
      return { dx, dy, dz };
    },

    normal(px, pz) {
      let nx: FloatNode = float(0);
      let ny: FloatNode = float(1);
      let nz: FloatNode = float(0);
      for (const t of terms) {
        const phase = phaseOf(t, px, pz);
        const wa = t.amp.mul(t.wv.w);
        const cwa = cos(phase).mul(wa);
        nx = nx.sub(cwa.mul(t.dirX));
        nz = nz.sub(cwa.mul(t.dirZ));
        ny = ny.sub(sin(phase).mul(wa).mul(t.wv.q));
      }
      return normalize(vec3(nx, ny, nz));
    },

    jacobian(px, pz) {
      let jxx: FloatNode = float(1);
      let jzz: FloatNode = float(1);
      let jxz: FloatNode = float(0);
      for (const t of terms) {
        const phase = phaseOf(t, px, pz);
        const qaw = sin(phase).mul(t.amp).mul(t.wv.q * t.wv.w);
        jxx = jxx.sub(qaw.mul(t.dirX).mul(t.dirX));
        jzz = jzz.sub(qaw.mul(t.dirZ).mul(t.dirZ));
        jxz = jxz.sub(qaw.mul(t.dirX).mul(t.dirZ));
      }
      return jxx.mul(jzz).sub(jxz.mul(jxz));
    },
  };
}
